import { X } from 'lucide-react-native';
import { memo } from 'react';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

type ExpertRelaySendConfirmProps = {
  /** Live expert name shown in the question and used to label the reply. */
  expertName: string;
  onChangeExpertName: (next: string) => void;
  /** Yes — send the pending reply attributed to the named expert. */
  onSendAsExpert: () => void;
  /** No — send the pending reply attributed to Primum Support (the safe default). */
  onSendAsPrimumSupport: () => void;
  /** Discard the pending reply and return to editing. */
  onCancel: () => void;
  isSending: boolean;
};

/**
 * Single-row attribution prompt shown in the composer toolbar after Send is
 * pressed on an anonymous Primum-routed inquiry where the reply can be
 * attributed to an expert.
 *
 * Pressing Send opens this prompt instead of sending, so the attribution choice
 * is impossible to forget. It reads as a question — "Send as Dr. X?" — with the
 * expert name editable in place so one reply can carry a different expert
 * without touching the inquiry. Yes sends as the expert; No sends as Primum
 * Support, the safe default; Cancel returns to editing. The name field is
 * height-capped so the row never grows the composer. Presentational only — the
 * caller owns the pending message and the send.
 */
export const ExpertRelaySendConfirm = memo(function ExpertRelaySendConfirm({
  expertName,
  onChangeExpertName,
  onSendAsExpert,
  onSendAsPrimumSupport,
  onCancel,
  isSending,
}: ExpertRelaySendConfirmProps) {
  const trimmedName = expertName.trim();

  const { colors, isDarkScheme } = useColors();

  const nameColor = isDarkScheme ? colors.typography[300] : colors.typography[700];

  return (
    <HStack
      className="h-12 w-full items-center gap-1.5 pl-3"
      accessibilityLiveRegion="polite"
      {...getAccessibilityProps({ label: 'Choose how to send this reply' })}>
      <Text className="font-normal text-sm text-typography-600">Send as</Text>

      {/* h-10 is a floor, not a style choice: verified on-device that below it,
          Android clips the ascenders off this field's typed text — invisible,
          not just tight — independent of includeFontPadding/textAlignVertical/
          lineHeight overrides, none of which affect it. iOS has room to spare
          here. */}
      <Input variant="outline" size="sm" className="h-10 flex-1 border-0" isDisabled={isSending}>
        <InputField
          value={expertName}
          onChangeText={onChangeExpertName}
          placeholder="Expert name"
          className="font-medium text-base text-typography-700"
          style={{ color: nameColor }}
          {...getAccessibilityProps({ label: 'Expert name to label this reply with' })}
        />
      </Input>

      <HStack className="items-center gap-2">
        <Pressable
          onPress={onSendAsExpert}
          disabled={isSending}
          hitSlop={ICON_BUTTON_HIT_SLOP}
          className="rounded-md px-1.5 py-1 active:opacity-70 data-[hover=true]:bg-background-50"
          {...getAccessibilityProps({
            role: 'button',
            label: `Send as ${trimmedName || 'expert'}`,
          })}>
          <Text size="sm" className="font-medium text-typography-900">
            Expert
          </Text>
        </Pressable>

        <Pressable
          onPress={onSendAsPrimumSupport}
          disabled={isSending}
          hitSlop={ICON_BUTTON_HIT_SLOP}
          className="rounded-md px-1.5 py-1 active:opacity-70 data-[hover=true]:bg-background-50"
          {...getAccessibilityProps({ role: 'button', label: 'Send as Primum Support' })}>
          <Text size="sm" className="font-medium text-typography-900">
            Support
          </Text>
        </Pressable>

        <Pressable
          onPress={onCancel}
          disabled={isSending}
          className="h-12 w-12 items-center justify-center active:opacity-70 data-[hover=true]:opacity-80"
          {...getAccessibilityProps({ role: 'button', label: 'Cancel and keep editing' })}>
          <Icon as={X} size="lg" className="text-typography-500" />
        </Pressable>
      </HStack>
    </HStack>
  );
});

ExpertRelaySendConfirm.displayName = 'ExpertRelaySendConfirm';
